import React, { useEffect, useState } from "react";
import {Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, RotateCw } from "lucide-react";
import {
  BedrockDailyCost,
  BedrockDailyCostResponse,
  PaginationData,
} from "../../../../types";
import { getBedrockDailyCost } from "../../../../service/apiService";

const ITEMS_PER_PAGE = 7;

const formatMoney = (amount: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount || 0);
  } catch {
    return `${(amount || 0).toFixed(4)} ${currency}`;
  }
};

// Every service the API returns is a Bedrock service (per-model
// "... (Bedrock Edition)" lines plus generic "Bedrock" / "Bedrock AgentCore");
// account-level lines such as Tax are filtered out server-side. So every one
// gets a column, and the columns add up exactly to the Total.
const getUsageTypes = (daily: BedrockDailyCost[]): string[] => {
  const types = new Set<string>();
  daily.forEach((row) =>
    Object.keys(row.cost_data || {}).forEach((t) => types.add(t))
  );
  return Array.from(types).sort();
};

// Strips the "(Bedrock Edition)" suffix for a readable column header. AWS
// returns "Claude Sonnet 4.6 ( Bedrock Edition)" -- note the space *inside* the
// paren -- so whitespace has to be optional on both sides, and "Amazon" is
// optional too. Full name preserved in the title attribute for hover tooltip.
const cleanServiceName = (name: string): string =>
  name.replace(/\s*\(\s*(?:Amazon\s+)?Bedrock\s+Edition\s*\)/gi, "").trim();

const BedrockCostTable = () => {
  const [daily, setDaily] = useState<BedrockDailyCost[]>([]);
  const [currency, setCurrency] = useState("USD");
  const [totalCost, setTotalCost] = useState(0);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    items_per_page: ITEMS_PER_PAGE,
    total_pages: 1,
    has_next: false,
    next_page: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDailyCost = async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result: BedrockDailyCostResponse = await getBedrockDailyCost(
        page,
        ITEMS_PER_PAGE
      );
      // Most recent day first
      const sorted = [...(result.daily || [])].sort((a, b) =>
        a.date > b.date ? -1 : 1
      );
      setDaily(sorted);
      setCurrency(result.currency || "USD");
      setTotalCost(result.total_cost || 0);
      if (result.pagination) setPagination(result.pagination);
    } catch (err) {
      console.error("Error fetching Bedrock daily cost:", err);
      setError("Failed to fetch Bedrock cost data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyCost(pagination.page);// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // ✅ Dynamic model columns — only models used on current page days
  const usageTypes = getUsageTypes(daily);

  return (
    <Disclosure as="div" className="py-2">
      <DisclosureButton className="group flex w-full items-center justify-between">
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Bedrock Cost Report (daily)
        </span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="mt-4 text-sm text-gray-600">

        {/* Data freshness notice */}
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-blue-50 px-4 py-2 text-xs text-blue-700">
          <span>ℹ️</span>
          <span>
            Cost data is sourced from AWS Cost Explorer and lags approximately{" "}
            <strong>24 hours</strong>. Recent days are marked{" "}
            <strong>Provisional</strong> until finalized (~48h after the usage
            date).
          </span>
        </div>

        {/* Total cost badge */}
        {!isLoading && !error && daily.length > 0 && (
          <div className="mb-4 inline-block rounded-lg bg-violet-50 px-4 py-2">
            <span className="text-violet-600 font-medium">
              Total (this page):{" "}
            </span>
            <span className="font-bold text-violet-700">
              {formatMoney(totalCost, currency)}
            </span>
          </div>
        )}

        {/* Pagination controls */}
        <div className="mb-4 flex justify-between items-center">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1 || isLoading}
            className={`inline-flex justify-center items-center py-2 px-4
              border border-transparent shadow-sm text-sm font-medium
              rounded-md text-white ${
                pagination.page === 1 || isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2
              focus:ring-violet-500`}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={!pagination.has_next || isLoading}
            className={`inline-flex justify-center items-center py-2 px-4
              border border-transparent shadow-sm text-sm font-medium
              rounded-md text-white ${
                !pagination.has_next || isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2
              focus:ring-violet-500`}
          >
            Next
          </button>
        </div>

        {/* Table */}
        <div className="mt-2 overflow-auto rounded-lg border border-gray-200 max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center space-x-2 p-4">
              <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
              <span>Loading...</span>
            </div>
          ) : error ? (
            <div className="p-4">Error: {error}</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-20">
                <tr>
                  {/* Date — sticky left */}
                  <th className="sticky left-0 z-30 bg-gray-100 border-r
                    border-gray-200 px-6 py-3 text-left text-xs font-medium
                    text-gray-500 uppercase tracking-wider">
                    Date
                  </th>

                  {/* Dynamic Bedrock model columns — per page */}
                  {usageTypes.map((type) => (
                    <th
                      key={type}
                      title={type}
                      className="px-6 py-3 text-center text-xs font-medium
                        text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {cleanServiceName(type)}
                    </th>
                  ))}

                  {/* Total */}
                  <th className="px-6 py-3 text-center text-xs font-medium
                    text-gray-500 uppercase tracking-wider">
                    Total
                  </th>

                  {/* Status */}
                  <th className="px-6 py-3 text-center text-xs font-medium
                    text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {daily.length > 0 ? (
                  daily.map((row) => (
                    <tr key={row.date}>
                      {/* Date */}
                      <td className="sticky left-0 z-10 bg-white border-r
                        border-gray-200 px-6 py-4 whitespace-nowrap
                        text-sm text-gray-900">
                        {row.date}
                      </td>

                      {/* Dynamic Bedrock model cells */}
                      {usageTypes.map((type) => (
                        <td
                          key={`${row.date}-${type}`}
                          className="px-6 py-4 text-center whitespace-nowrap
                            text-sm text-gray-900"
                        >
                          {row.cost_data && row.cost_data[type] != null
                            ? formatMoney(row.cost_data[type], row.currency)
                            : "—"}
                        </td>
                      ))}

                      {/* Total */}
                      <td className="px-6 py-4 text-center whitespace-nowrap
                        text-sm font-medium text-gray-900">
                        {formatMoney(row.total_cost, row.currency)}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 text-center whitespace-nowrap
                        text-sm">
                        {row.is_final ? (
                          <span className="inline-flex items-center px-2.5
                            py-0.5 rounded-full text-xs font-medium
                            bg-green-100 text-green-800">
                            Final
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5
                            py-0.5 rounded-full text-xs font-medium
                            bg-amber-100 text-amber-800">
                            Provisional
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={usageTypes.length + 3}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No Bedrock cost data available yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};

export default BedrockCostTable;